import { View, Text, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  continuation: {
    marginTop: 12,
  },

  table: {
    border: '1px solid #EDEFF4',
    borderRadius: 6,
    fontFamily: 'Janna',
  },

  tableHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTopRightRadius: 6,
    borderTopLeftRadius: 6,
    paddingVertical: 12,
    paddingHorizontal: 8,
    fontSize: 8,
    fontWeight: 'bold',
    color: '#271060',
    backgroundColor: '#EDEFF4',
  },

  tableRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 8,
    fontSize: 8,
    color: '#271060',
  },

  tableRowWithBg: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 8,
    fontSize: 8,
    color: '#271060',
    backgroundColor: '#F6F7FA',
  },

  tableTotals: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 8,
    fontSize: 8,
    fontWeight: 'bold',
    color: '#271060',
    backgroundColor: '#EDEFF4',
  },
});

export type ReportsTableConfig = {
  key: string;
  title: string;
  dataSourceKey: string;
  width?: string;
  textAlign?: 'left' | 'center' | 'right' | 'justify';
};

export type ReportsDataSource = {
  [K in ReportsTableConfig['dataSourceKey']]: string | number | null;
};

const ROWS_PER_PAGE = 30;

const ReportsTable = ({
  tableConfig,
  dataSource,
  tableTotals,
  rowStyle,
  rowStyleWithBg,
  tableHeaderStyle,
}: {
  tableConfig: ReportsTableConfig[];
  dataSource: ReportsDataSource[];
  tableTotals?: ReportsDataSource;
  rowStyle?: { [key: string]: string | number };
  rowStyleWithBg?: { [key: string]: string | number };
  tableHeaderStyle?: { [key: string]: string | number };
}) => {
  const chunks = dataSource.length
    ? Array.from({ length: Math.ceil(dataSource.length / ROWS_PER_PAGE) }, (_, index) =>
        dataSource.slice(index * ROWS_PER_PAGE, (index + 1) * ROWS_PER_PAGE),
      )
    : [[]];

  return (
    <View>
      {chunks.map((chunk, chunkIndex) => (
        <View
          key={chunkIndex}
          style={chunkIndex > 0 ? [styles.table, styles.continuation] : styles.table}
          wrap={false}
          break={chunkIndex > 0}
        >
          <View style={tableHeaderStyle ? tableHeaderStyle : styles.tableHeader}>
            {tableConfig.map(({ title, key, width: columnWidth, textAlign }, index) => (
              <View
                key={key}
                style={{
                  width: columnWidth ? columnWidth : '100%',
                  textAlign: textAlign ?? (index === 0 ? undefined : 'center'),
                }}
              >
                <Text>{title}</Text>
              </View>
            ))}
          </View>

          {chunk.map((it, chunkRowIndex) => {
            const rowIndex = chunkIndex * ROWS_PER_PAGE + chunkRowIndex;

            return (
              <View
                key={rowIndex}
                style={rowIndex % 2 ? rowStyleWithBg ?? styles.tableRowWithBg : rowStyle ?? styles.tableRow}
              >
                {tableConfig.map(({ dataSourceKey, width: columnWidth, textAlign }, columnIndex) => (
                  <View
                    key={dataSourceKey}
                    style={{
                      width: columnWidth ? columnWidth : '100%',
                      textAlign: textAlign ?? (columnIndex === 0 ? undefined : 'center'),
                    }}
                  >
                    <Text>{it[dataSourceKey]}</Text>
                  </View>
                ))}
              </View>
            );
          })}

          {tableTotals && chunkIndex === chunks.length - 1 && (
            <View style={styles.tableTotals}>
              {Object.keys(tableTotals).map((key, index) => (
                <View
                  style={{
                    width: '100%',
                    textAlign: tableConfig[index]?.textAlign ?? (index === 0 ? undefined : 'center'),
                  }}
                  key={key}
                >
                  <Text>{tableTotals[key]}</Text>
                </View>
              ))}
            </View>
          )}
        </View>
      ))}
    </View>
  );
};

export default ReportsTable;
